/**
 * Procedural lofi medieval ambient music using Web Audio API.
 * Generates pentatonic melodies with reverb, pads, and gentle rhythm.
 * No external files needed — everything synthesized.
 */

// D minor pentatonic scale (medieval vibe)
const SCALE = [
  146.83, // D3
  174.61, // F3
  196.00, // G3
  220.00, // A3
  261.63, // C4
  293.66, // D4
  349.23, // F4
  392.00, // G4
  440.00, // A4
  523.25, // C5
];

const PAD_NOTES = [146.83, 220.00, 293.66]; // D3, A3, D4 drone chord
const BPM = 55; // slow, contemplative
const BEAT_DURATION = 60 / BPM;

export type AmbientMusic = {
  start: () => void;
  stop: () => void;
  setVolume: (v: number) => void;
  isPlaying: boolean;
};

export function createAmbientMusic(): AmbientMusic {
  let ctx: AudioContext | null = null;
  let masterGain: GainNode | null = null;
  let isPlaying = false;
  let nextNoteTime = 0;
  let schedulerInterval: ReturnType<typeof setInterval> | null = null;
  let padOscillators: OscillatorNode[] = [];
  let stepIndex = 0;

  function init() {
    ctx = new AudioContext();
    masterGain = ctx.createGain();
    masterGain.gain.value = 0.3;

    // Reverb via convolution (simple impulse response)
    const convolver = ctx.createConvolver();
    const reverbBuffer = createReverbImpulse(ctx, 3, 2);
    convolver.buffer = reverbBuffer;

    // Dry/wet mix
    const dryGain = ctx.createGain();
    dryGain.gain.value = 0.6;
    const wetGain = ctx.createGain();
    wetGain.gain.value = 0.4;

    masterGain.connect(dryGain).connect(ctx.destination);
    masterGain.connect(convolver).connect(wetGain).connect(ctx.destination);
  }

  function start() {
    if (isPlaying) return;
    if (!ctx) init();
    if (!ctx) return;

    if (ctx.state === "suspended") {
      ctx.resume();
    }

    isPlaying = true;
    nextNoteTime = ctx.currentTime + 0.1;

    // Start pad drone
    startPad();

    // Schedule melody notes
    schedulerInterval = setInterval(() => scheduleNotes(), 100);
  }

  function stop() {
    if (!isPlaying) return;
    isPlaying = false;

    if (schedulerInterval) {
      clearInterval(schedulerInterval);
      schedulerInterval = null;
    }

    // Fade out pad
    padOscillators.forEach((osc) => {
      try { osc.stop(ctx!.currentTime + 0.5); } catch { /* already stopped */ }
    });
    padOscillators = [];
  }

  function setVolume(v: number) {
    if (masterGain) {
      masterGain.gain.setTargetAtTime(v, ctx!.currentTime, 0.1);
    }
  }

  function startPad() {
    if (!ctx || !masterGain) return;

    PAD_NOTES.forEach((freq) => {
      const osc = ctx!.createOscillator();
      osc.type = "sine";
      osc.frequency.value = freq;

      const gain = ctx!.createGain();
      gain.gain.value = 0.06; // very quiet pad

      // Slow LFO for movement
      const lfo = ctx!.createOscillator();
      lfo.frequency.value = 0.1 + Math.random() * 0.05;
      const lfoGain = ctx!.createGain();
      lfoGain.gain.value = 3; // slight pitch wobble
      lfo.connect(lfoGain).connect(osc.frequency);
      lfo.start();

      osc.connect(gain).connect(masterGain!);
      osc.start();
      padOscillators.push(osc);
    });
  }

  function scheduleNotes() {
    if (!ctx || !masterGain || !isPlaying) return;

    const lookAhead = 0.2; // seconds

    while (nextNoteTime < ctx.currentTime + lookAhead) {
      playMelodyNote(nextNoteTime);
      stepIndex++;

      // Irregular rhythm — some beats are rests
      const beatMultiplier = stepIndex % 7 === 0 ? 2 : stepIndex % 5 === 0 ? 1.5 : 1;
      nextNoteTime += BEAT_DURATION * beatMultiplier;
    }
  }

  function playMelodyNote(time: number) {
    if (!ctx || !masterGain) return;

    // Sometimes rest (30% chance)
    const hash = Math.sin(stepIndex * 12.9898 + 78.233) * 43758.5453;
    const rand = hash - Math.floor(hash);
    if (rand < 0.3) return;

    // Pick note from scale (pseudo-random but musical)
    const noteIdx = Math.floor(((Math.sin(stepIndex * 7.31) + 1) / 2) * SCALE.length);
    const freq = SCALE[noteIdx % SCALE.length];

    const osc = ctx.createOscillator();
    // Alternate between sine and triangle for variety
    osc.type = stepIndex % 3 === 0 ? "triangle" : "sine";
    osc.frequency.value = freq;

    // ADSR envelope
    const gain = ctx.createGain();
    const attack = 0.05;
    const decay = 0.1;
    const sustain = 0.12;
    const release = BEAT_DURATION * 0.8;

    gain.gain.setValueAtTime(0, time);
    gain.gain.linearRampToValueAtTime(0.15, time + attack);
    gain.gain.linearRampToValueAtTime(sustain, time + attack + decay);
    gain.gain.linearRampToValueAtTime(0, time + attack + decay + release);

    // Filter for warmth
    const filter = ctx.createBiquadFilter();
    filter.type = "lowpass";
    filter.frequency.value = 1200 + rand * 800;
    filter.Q.value = 1;

    osc.connect(filter).connect(gain).connect(masterGain);
    osc.start(time);
    osc.stop(time + attack + decay + release + 0.1);
  }

  return {
    start,
    stop,
    setVolume,
    get isPlaying() { return isPlaying; },
  };
}

/**
 * Create synthetic reverb impulse response.
 */
function createReverbImpulse(ctx: AudioContext, duration: number, decay: number): AudioBuffer {
  const sampleRate = ctx.sampleRate;
  const length = sampleRate * duration;
  const buffer = ctx.createBuffer(2, length, sampleRate);

  for (let channel = 0; channel < 2; channel++) {
    const data = buffer.getChannelData(channel);
    for (let i = 0; i < length; i++) {
      data[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / length, decay);
    }
  }

  return buffer;
}
